/* eslint-disable @typescript-eslint/no-unused-vars */
import { memo, ReactNode } from 'react';
import { Stack, TextField as MuiTextField, OutlinedTextFieldProps, Typography, SxProps, Theme } from '@mui/material';

export type ResponsiveStyleValue<T> = T | Array<T | null> | { [key: string]: T | null };
interface IDialogTextFieldProps extends Partial<OutlinedTextFieldProps> {
  label?: string | ReactNode;
  labelWidth?: number | string;
  width?: number | string;
  marginBottom?: string;
  text?: string | Date | null;
  maxWidth?: number | string;
  stackDirection?: ResponsiveStyleValue<'row' | 'row-reverse' | 'column' | 'column-reverse'>;
  labelSx?: SxProps;
  stackSx?: SxProps;
}

function DialogTextField({
  label,
  labelWidth = '100px',
  width,
  marginBottom = '20px',
  text,
  stackDirection = { xs: 'column', sm: 'column', md: 'row' },
  labelSx = {},
  stackSx = {},
  ...others
}: IDialogTextFieldProps) {
  const labelStyle: SxProps<Theme> = {
    color: 'mind.black.A0',
    minWidth: labelWidth,
    maxWidth: labelWidth,
    typography: 's13w800',
  };
  const paddingStyle = others?.multiline === true ? '0px' : '10px 16px';
  return (
    <Stack
      sx={{ marginBottom, marginTop: '1px', ...stackSx }}
      spacing="8px"
      direction={stackDirection}
      alignItems="center"
    >
      {label && (
        <Typography
          sx={{
            ...labelStyle,
            ...labelSx,
            maxWidth: { xs: '100%', sm: '100%', md: labelWidth },
            width: { xs: '100%', sm: '100%', md: labelWidth },
          }}
          noWrap
        >
          {label}
        </Typography>
      )}

      <MuiTextField
        {...others}
        value={text}
        autoComplete="off"
        sx={theme => ({
          width: width || '100%',
          height: others.rows === undefined ? 40 : 'auto',
          maxWidth: others.maxWidth || '100%',
          border: 'none',
          borderRadius: '4px',
          '& .MuiFormControl-root .MuiTextField-root .MuiInputBase-input': {
            padding: 1,
          },
          '& .MuiInputBase-root': {
            border: others.error
              ? `1px solid ${theme.palette.error.main}`
              : `1px solid ${theme.palette.mind.grey.border2}`,
            ':hover': {
              border: others.error
                ? `1px solid ${theme.palette.error.main}`
                : `1px solid ${theme.palette.mind.grey.border2}`,
            },
            '&.Mui-focused': {
              border: others.error
                ? `1px solid ${theme.palette.error.main}`
                : `2px solid ${theme.palette.primary.main}`,
            },
            '&.Mui-disabled': {
              textFillColor: 'rgba(0, 0, 0, 0.38)',
              backgroundColor: '#E0E0E0',
            },
          },
          '& .MuiInputBase-input': {
            color: theme.palette.mind.black.A10,
            padding: paddingStyle,
            lineHeight: '20px',
            typography: 's13w500',
            '&::placeholder': {
              color: theme.palette.mind.grey.placeholder,
              opacity: 1,
              typography: 's13w500',
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
            textFillColor: 'rgba(0, 0, 0, 0.38)',
          },
        })}
      />
    </Stack>
  );
}

export default memo(DialogTextField);
